import {groupByUniqueKey} from '@benchling/js-utils';
import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useMemo, useState} from 'react';

import AddLayerButton from 'samples/plate-maps/display-layer-controls/AddLayerButton';
import {
  PLATE_MAP_MAX_WELL_SCHEMA_FIELD_LAYERS,
  SchematizedAttribute,
  SchematizedDisplayLayerMetadata,
} from 'samples/plate-maps/PlateMapConstants';
import MultiSelectSearchableDropdown from 'ui-library/dropdowns/MultiSelectSearchableDropdown';
import Menu from 'ui-library/menus/Menu';
import PopoverIcon from 'ui-library/PopoverIcon';

export function WellSchemaFieldDropdown({
  onToggleLayer,
  schemaFieldOptions,
  selectedValues,
}: {
  onToggleLayer: ({
    newSelectedIds,
    onLaunchConfirmDeleteModal,
    toggledAttribute,
  }: {
    newSelectedIds: Array<string>;
    onLaunchConfirmDeleteModal: () => void;
    toggledAttribute: SchematizedAttribute;
  }) => void;
  schemaFieldOptions: Array<SchematizedDisplayLayerMetadata>;
  selectedValues: Array<string>;
}): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);

  const tooManySelected = selectedValues.length >= PLATE_MAP_MAX_WELL_SCHEMA_FIELD_LAYERS;
  const optionDatas = schemaFieldOptions
    // If an invalid layer has been removed, do not allow re-adding it from the dropdown
    .filter((option) => !option.outdated || selectedValues.includes(option.attribute.key))
    .map((schemaFieldOption) => ({
      name: schemaFieldOption.displayName,
      value: schemaFieldOption.attribute.key,
      icon: schemaFieldOption.icon,
      disabled: tooManySelected && !selectedValues.includes(schemaFieldOption.attribute.key),
    }));
  const attributesById = useMemo(
    () =>
      groupByUniqueKey(
        schemaFieldOptions.map((option) => option.attribute as SchematizedAttribute),
        (attribute) => attribute.key
      ),
    [schemaFieldOptions]
  );

  return (
    <MultiSelectSearchableDropdown
      closeOnInsideClick={false}
      closeOnOutsideClick={true}
      disabled={tooManySelected}
      dropdownToggleOverride={
        <AddLayerButton
          disabled={tooManySelected}
          onClick={() => {
            if (!tooManySelected) {
              setIsOpen(!isOpen);
            }
          }}
        />
      }
      isOpen={isOpen}
      menuProps={{width: Menu.WIDTHS.FIT_CONTENT}}
      onSelect={(newSelectedIds: Array<string>) => {
        const newIdSet = new Set(newSelectedIds);
        const oldIdSet = new Set(selectedValues);
        const [toggledId] =
          newSelectedIds.length > selectedValues.length
            ? newSelectedIds.filter((id) => !oldIdSet.has(id))
            : selectedValues.filter((id) => !newIdSet.has(id));
        onToggleLayer({
          newSelectedIds,
          onLaunchConfirmDeleteModal: () => {
            setIsOpen(false);
          },
          toggledAttribute: attributesById[toggledId],
        });
        if (newSelectedIds.length >= PLATE_MAP_MAX_WELL_SCHEMA_FIELD_LAYERS) {
          setIsOpen(false);
        }
      }}
      onToggle={(newIsOpen) => {
        if (!tooManySelected) {
          setIsOpen(newIsOpen);
        } else if (isOpen) {
          setIsOpen(false);
        }
      }}
      optionDatas={optionDatas}
      noneSelectedMessage=""
      selectedValues={selectedValues}
    />
  );
}

export function WellSchemaFieldDeleteButton({
  schemaFieldId,
  onRemoveLayer,
}: {
  schemaFieldId: string;
  onRemoveLayer: (idToRemove: string) => void;
}): JSX.Element {
  return (
    <PopoverIcon
      icon="xmark"
      renderContent={() => 'Excluded layers will not be updated on the plate'}
      onClick={(e) => {
        e.stopPropagation(); // Don't also select the attribute we're about to remove.
        onRemoveLayer(schemaFieldId);
      }}
      className={css(styles.removeLayerIcon)}
    />
  );
}

const styles = StyleSheet.create({
  removeLayerIcon: {
    color: Color.GRAY_500,
  },
});
